import {
  pickOverviewControlRecord,
  resolveOverviewControlView,
  resolveOverviewPlaceLabel,
} from '../safetyCases/safetyCaseOverviewPresenter';
import {
  customerScanCameraLabel,
  customerScanZoneLabel,
} from './safetyScanProductPresentation';
import { parseSiteDateTimeLocalInput } from '../siteTime/siteTime';

function asText(value: unknown): string {
  return String(value || '').trim();
}

const WORKFLOW_SCOPE_RE = /^([^|]+)\|([^|]+)\|([^|]+)\|([^|]+)$/;

export function parseWorkflowScope(workflowId?: string | null): {
  risk_type: string;
  zone_id: string;
  camera_id: string;
  device_id: string;
} {
  const match = asText(workflowId).match(WORKFLOW_SCOPE_RE);
  if (!match) {
    return { risk_type: '', zone_id: '', camera_id: '', device_id: '' };
  }
  return {
    risk_type: match[1],
    zone_id: match[2],
    camera_id: match[3],
    device_id: match[4],
  };
}

export function resolveScanCameraId(args: {
  scopeCameraId?: string | null;
  workflowId?: string | null;
  linkedCameraId?: string | null;
  filterCameraId?: string | null;
}): string {
  return asText(args.scopeCameraId)
    || asText(parseWorkflowScope(args.workflowId).camera_id)
    || asText(args.linkedCameraId)
    || asText(args.filterCameraId);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export type SafetyScanTimeWindow = {
  start: string | null;
  end: string | null;
};

export type SafetyScanPlaceContext = {
  zoneLabel: string;
  cameraLabel: string;
  locationLine: string;
  areaLabel: string;
  technicalIds: string[];
};

export type SafetyScanControlContext = {
  recorded: boolean;
  description: string;
  ownerLabel: string;
  statusLabel: string;
  implementedAt: string | null;
  dueDate: string | null;
  validatedAt: string | null;
  completedAt: string | null;
};

export type SafetyScanReportContext = {
  reportId: string;
  workflowId: string;
  safetyCaseId: string;
  timeWindow: SafetyScanTimeWindow;
  place: SafetyScanPlaceContext;
  control: SafetyScanControlContext;
};

function readNestedWindow(source: unknown): SafetyScanTimeWindow {
  const record = asRecord(source);
  if (!record) return { start: null, end: null };
  const nested = asRecord(record.time_window)
    || asRecord(record.window)
    || record;
  const start = asText(nested.start || nested.from);
  const end = asText(nested.end || nested.to);
  return {
    start: start || null,
    end: end || null,
  };
}

export function readPersistedReportWindow(persistedReport?: unknown): SafetyScanTimeWindow {
  const report = asRecord(persistedReport);
  if (!report) return { start: null, end: null };
  const fromAnchor = readNestedWindow(report.workflow_anchor);
  if (fromAnchor.start && fromAnchor.end) return fromAnchor;
  return readNestedWindow(report.time_window || report);
}

export function resolvePersistedReportWindow(args: {
  persistedReport?: unknown;
  queueWindow?: { start?: string | null; end?: string | null } | null;
  urlStart?: string | null;
  urlEnd?: string | null;
}): SafetyScanTimeWindow {
  const fromReport = readPersistedReportWindow(args.persistedReport);
  if (fromReport.start && fromReport.end) return fromReport;
  const queueStart = asText(args.queueWindow?.start);
  const queueEnd = asText(args.queueWindow?.end);
  if (queueStart && queueEnd) {
    return { start: queueStart, end: queueEnd };
  }
  const urlStart = asText(args.urlStart);
  const urlEnd = asText(args.urlEnd);
  if (urlStart && urlEnd) {
    return { start: urlStart, end: urlEnd };
  }
  return {
    start: fromReport.start || queueStart || urlStart || null,
    end: fromReport.end || queueEnd || urlEnd || null,
  };
}

export function resolveDraftScanHandoffWindow(args: {
  hasPersistedDraft: boolean;
  queueWindow?: { start?: string | null; end?: string | null } | null;
  reportAnchorWindow?: unknown;
  caseProjectionWindow?: unknown;
  caseSeedWindow?: unknown;
}): SafetyScanTimeWindow {
  const queue = readNestedWindow(args.queueWindow);
  if (queue.start && queue.end) return queue;
  const fromAnchor = readNestedWindow(args.reportAnchorWindow);
  if (fromAnchor.start && fromAnchor.end) return fromAnchor;
  const fromProjection = readNestedWindow(args.caseProjectionWindow);
  if (fromProjection.start && fromProjection.end) return fromProjection;
  if (!args.hasPersistedDraft) {
    return readNestedWindow(args.caseSeedWindow);
  }
  return {
    start: queue.start || fromAnchor.start || fromProjection.start || null,
    end: queue.end || fromAnchor.end || fromProjection.end || null,
  };
}

export function resolveSafetyScanPlace(args: {
  zoneId?: string | null;
  zoneLabel?: string | null;
  areaLabel?: string | null;
  cameraId?: string | null;
  cameraLabel?: string | null;
  locationLabel?: string | null;
}): SafetyScanPlaceContext {
  const zoneId = asText(args.zoneId);
  const cameraId = asText(args.cameraId);
  const zoneLabel = customerScanZoneLabel(zoneId || args.zoneLabel)
    || customerScanZoneLabel(args.areaLabel)
    || asText(args.zoneLabel)
    || asText(args.areaLabel);
  const cameraLabel = customerScanCameraLabel(cameraId || args.cameraLabel)
    || asText(args.cameraLabel);
  const overviewPlace = resolveOverviewPlaceLabel({
    zoneId,
    zoneLabel: zoneLabel || args.zoneLabel,
    areaLabel: args.areaLabel,
    cameraId,
    cameraLabel: cameraLabel || args.cameraLabel,
    locationLabel: args.locationLabel,
  });
  const cameraArea = /warehouse room b/i.test(overviewPlace)
    ? 'Warehouse Room B'
    : (/warehouse room b/i.test(cameraLabel) ? 'Warehouse Room B' : '');
  const locationLine = [zoneLabel, cameraArea || cameraLabel].filter((part, index, all) => {
    const text = asText(part);
    if (!text) return false;
    return all.findIndex((candidate) => asText(candidate).toLowerCase() === text.toLowerCase()) === index;
  }).join(' · ');
  const technicalIds = [zoneId, cameraId].filter(Boolean);
  return {
    zoneLabel,
    cameraLabel,
    locationLine: locationLine || overviewPlace || zoneLabel || cameraLabel,
    areaLabel: locationLine || overviewPlace || zoneLabel,
    technicalIds,
  };
}

function firstTimestamp(...values: unknown[]): string | null {
  for (const value of values) {
    const text = asText(value);
    if (text) return text;
  }
  return null;
}

export function resolveSafetyScanControl(args: {
  recommendations?: unknown;
  actions?: unknown;
  workflowPrimaryAction?: Record<string, unknown> | null;
  lifecycleControlRecorded?: boolean;
}): SafetyScanControlContext {
  const workflowAction = asRecord(args.workflowPrimaryAction);
  const actions = [
    ...(Array.isArray(args.actions) ? args.actions : []),
    ...(workflowAction ? [workflowAction] : []),
  ];
  const record = pickOverviewControlRecord({
    actions,
    recommendations: Array.isArray(args.recommendations)
      ? args.recommendations
      : [],
  });
  const overview = resolveOverviewControlView({
    actions,
    recommendations: Array.isArray(args.recommendations) ? args.recommendations : [],
    lifecycleControlRecorded: args.lifecycleControlRecorded,
  });
  const completedAt = firstTimestamp(record?.completed_at);
  const validatedAt = firstTimestamp(record?.validated_at);
  const implementedAt = firstTimestamp(record?.implemented_at, completedAt, validatedAt);
  const dueDate = firstTimestamp(record?.due_date);
  const recorded = overview.case !== 'missing';
  return {
    recorded,
    description: recorded
      ? (overview.title || asText(record?.title) || asText(record?.description) || 'Control recorded')
      : '',
    ownerLabel: asText(overview.owner) || asText(record?.assignee) || asText(record?.owner),
    statusLabel: asText(overview.status) || asText(record?.status),
    implementedAt,
    dueDate,
    validatedAt,
    completedAt,
  };
}

export function resolveSafetyScanReportContext(args: {
  reportId?: string | null;
  workflowId?: string | null;
  safetyCaseId?: string | null;
  persistedReport?: unknown;
  queueWindow?: { start?: string | null; end?: string | null } | null;
  urlStart?: string | null;
  urlEnd?: string | null;
  zoneId?: string | null;
  zoneLabel?: string | null;
  areaLabel?: string | null;
  cameraId?: string | null;
  cameraLabel?: string | null;
  locationLabel?: string | null;
  recommendations?: unknown;
  actions?: unknown;
  workflowPrimaryAction?: Record<string, unknown> | null;
  lifecycleControlRecorded?: boolean;
}): SafetyScanReportContext {
  const persisted = asRecord(args.persistedReport);
  const anchor = asRecord(persisted?.workflow_anchor);
  const scope = asRecord(anchor?.scope);
  const reportId = asText(args.reportId) || asText(persisted?.report_id);
  const workflowId = asText(args.workflowId) || asText(persisted?.workflow_id) || asText(anchor?.workflow_id);
  const parsed = parseWorkflowScope(workflowId);
  const zones = scope?.zones;
  const scopeZones = Array.isArray(zones) ? [...zones] : [];
  return {
    reportId,
    workflowId,
    safetyCaseId: asText(args.safetyCaseId)
      || asText(persisted?.finding_id)
      || asText(persisted?.safety_case_id)
      || asText(anchor?.safety_case_id),
    timeWindow: resolvePersistedReportWindow({
      persistedReport: args.persistedReport,
      queueWindow: args.queueWindow,
      urlStart: args.urlStart,
      urlEnd: args.urlEnd,
    }),
    place: resolveSafetyScanPlace({
      zoneId: asText(scope?.zone_id) || asText(scopeZones[0]) || parsed.zone_id || asText(args.zoneId),
      zoneLabel: args.zoneLabel,
      areaLabel: args.areaLabel,
      cameraId: resolveScanCameraId({
        scopeCameraId: asText(scope?.camera_id),
        workflowId,
        linkedCameraId: null,
        filterCameraId: asText(args.cameraId),
      }),
      cameraLabel: args.cameraLabel,
      locationLabel: args.locationLabel,
    }),
    control: resolveSafetyScanControl({
      recommendations: args.recommendations,
      actions: args.actions,
      workflowPrimaryAction: args.workflowPrimaryAction,
      lifecycleControlRecorded: args.lifecycleControlRecorded,
    }),
  };
}

export function hasCanonicalPersistedWindow(args: {
  persistedReport?: unknown;
  queueWindow?: { start?: string | null; end?: string | null } | null;
}): boolean {
  const window = resolvePersistedReportWindow({
    persistedReport: args.persistedReport,
    queueWindow: args.queueWindow,
  });
  return Boolean(window.start && window.end);
}

export function resolveWorkspaceSafetyScanReportContext(args: {
  reportId?: string | null;
  persistedReport?: unknown;
  queueWindow?: { start?: string | null; end?: string | null } | null;
  urlStart?: string | null;
  urlEnd?: string | null;
  workflowId?: string | null;
  safetyCaseId?: string | null;
  linkedCase?: {
    workflow_id?: string | null;
    safety_case_id?: string | null;
    finding_id?: string | null;
    zone_id?: string | null;
    camera_id?: string | null;
    scope?: Record<string, unknown> | null;
    recommendations?: unknown;
  } | null;
  zoneId?: string | null;
  cameraId?: string | null;
  areaLabel?: string | null;
  cameraLabel?: string | null;
  actions?: unknown;
  workflowPrimaryAction?: Record<string, unknown> | null;
  lifecycleControlRecorded?: boolean;
}): SafetyScanReportContext {
  const linked = args.linkedCase || null;
  const scope = linked?.scope && typeof linked.scope === 'object' ? linked.scope : {};
  const scopeZones = Array.isArray(scope.zones) ? scope.zones : [];
  const workflowId = args.workflowId || linked?.workflow_id || null;
  return resolveSafetyScanReportContext({
    reportId: args.reportId,
    workflowId,
    safetyCaseId: args.safetyCaseId || linked?.safety_case_id || linked?.finding_id,
    persistedReport: args.persistedReport,
    queueWindow: args.queueWindow,
    urlStart: args.urlStart,
    urlEnd: args.urlEnd,
    zoneId: asText(scope.zone_id) || asText(scopeZones[0]) || linked?.zone_id || args.zoneId,
    cameraId: resolveScanCameraId({
      scopeCameraId: asText(scope.camera_id),
      workflowId,
      linkedCameraId: linked?.camera_id,
      filterCameraId: args.cameraId,
    }),
    areaLabel: args.areaLabel,
    cameraLabel: args.cameraLabel,
    recommendations: linked?.recommendations,
    actions: args.actions,
    workflowPrimaryAction: args.workflowPrimaryAction,
    lifecycleControlRecorded: args.lifecycleControlRecorded,
  });
}

export type CanonicalDraftScopeSelection = {
  reportId: string;
  workflowId: string;
  zoneId: string;
  policyType: string;
  cameraId: string;
  windowStart: string | null;
  windowEnd: string | null;
  canApply: boolean;
};

export function resolveCanonicalDraftScopeSelection(args: {
  persistedReport?: unknown;
  workflowId?: string | null;
  reportId?: string | null;
  zoneDraft?: string | null;
  policyDraft?: string | null;
  cameraDraft?: string | null;
  windowStartDraft?: string | null;
  windowEndDraft?: string | null;
  windowEdited?: boolean;
  siteTimeZone?: string | null;
  urlCameraId?: string | null;
}): CanonicalDraftScopeSelection {
  const persisted = asRecord(args.persistedReport);
  const anchor = asRecord(persisted?.workflow_anchor);
  const scope = asRecord(anchor?.scope);
  const reportId = asText(args.reportId) || asText(persisted?.report_id);
  const workflowId = asText(args.workflowId) || asText(persisted?.workflow_id) || asText(anchor?.workflow_id);
  const parsed = parseWorkflowScope(workflowId);
  const persistedWindow = readPersistedReportWindow(args.persistedReport);
  const scopeZones = Array.isArray(scope?.zones) ? [...(scope?.zones as unknown[])] : [];
  const persistedZone = asText(scope?.zone_id) || asText(scopeZones[0]) || parsed.zone_id;
  const persistedPolicy = asText(scope?.risk_type) || asText(scope?.policy_type) || parsed.risk_type;
  const persistedCamera = resolveScanCameraId({
    scopeCameraId: asText(scope?.camera_id),
    workflowId,
    linkedCameraId: null,
    filterCameraId: asText(args.urlCameraId),
  });
  const zoneId = asText(args.zoneDraft) || persistedZone;
  const policyType = asText(args.policyDraft) || persistedPolicy;
  const cameraId = asText(args.cameraDraft) || persistedCamera;
  let windowStart = persistedWindow.start;
  let windowEnd = persistedWindow.end;
  if (args.windowEdited || !windowStart || !windowEnd) {
    const parsedStart = parseSiteDateTimeLocalInput(args.windowStartDraft, args.siteTimeZone)
      || asText(args.windowStartDraft);
    const parsedEnd = parseSiteDateTimeLocalInput(args.windowEndDraft, args.siteTimeZone)
      || asText(args.windowEndDraft);
    if (parsedStart) windowStart = parsedStart;
    if (parsedEnd) windowEnd = parsedEnd;
  }
  return {
    reportId,
    workflowId,
    zoneId,
    policyType,
    cameraId,
    windowStart,
    windowEnd,
    canApply: Boolean(cameraId),
  };
}
